import { gql, useApolloClient, useMutation } from '@apollo/client';
import ReactPixel from 'react-facebook-pixel';
import { useDispatch, useSelector } from 'react-redux';
import useLogError from '../common/useLogError';
import { addLocalCartItem, addProductLocalCart } from '../store/actions';
import {
  formatGTMDataForAddToCart,
  gtmTrackEvent
} from '../utils/gtmTrackEvent';
import { pinterestTrackEvent } from '../utils/pinterestTrackEvent';
import {
  formatSegmentDataForProductAddedEvent,
  segmentTrackEvent
} from '../utils/segmentTracking';

const emailForNewsLetter = localStorage.getItem('emailForNewsLetter');

function useCreateCartProduct() {
  const client = useApolloClient();
  const dispatch = useDispatch();
  const [logError] = useLogError();
  const isAuthenticated = useSelector(state => state.Auth.isAuthenticated);
  const [addToCart, { loading }] = useMutation(CREATE_CART_PRODUCT, {
    refetchQueries: () => [
      {
        query: GET_ALL_CART_PRODUCTS
      },
      {
        query: GET_TOTAL_CART_PRODUCTS
      }
    ]
  });

  const handleAddToCart = ({
    product,
    quantity = 1,
    source = '',
    extendPlan,
    extendPlanId = null
  }) => {
    if (!product.price || !product.sku) {
      logError({
        variables: {
          error: `AddToCart: Facebook Pixel issue | window.location: ${
            window.location.href
          } | ID: ${product.id} | SKU: ${product.sku} | Price: ${product.price}`
        }
      });
      return;
    }

    segmentTrackEvent(
      'Product Added',
      formatSegmentDataForProductAddedEvent(product, quantity)
    );
    gtmTrackEvent(formatGTMDataForAddToCart(product, quantity));
    ReactPixel.track('AddToCart', {
      content_type: 'product',
      content_ids: [product.sku],
      value: product.price,
      currency: 'USD'
    });
    pinterestTrackEvent('track', 'AddToCart', {
      product_id: product.id,
      value: product.price,
      order_quantity: 1,
      currency: 'USD'
    });

    if (!isAuthenticated) {
      if (product?.is_swatch) {
        dispatch(addLocalCartItem(product));
      } else {
        dispatch(addProductLocalCart(product, quantity, source, extendPlan));
      }

      if (emailForNewsLetter) {
        client.mutate({
          mutation: NOTIFY_KLAVIYO,
          variables: {
            email: emailForNewsLetter,
            event: 'ProductAddedToCart',
            product_id: product.id
          }
        });
      }
    } else {
      return addToCart({
        variables: {
          quantity: quantity,
          product_id: product.id,
          type: 'increase',
          source,
          extend_plan_id: extendPlanId
        }
      });
    }
  };

  return [handleAddToCart, { loading }];
}

export default useCreateCartProduct;

export const GET_TOTAL_CART_PRODUCTS = gql`
  query getTotalCartProducts {
    getTotalCartProducts {
      total
    }
  }
`;

const CREATE_CART_PRODUCT = gql`
  mutation createCartProduct(
    $product_id: ID!
    $quantity: Int!
    $type: String!
    $source: Int
    $extend_plan_id: String
  ) {
    createCartProduct(
      product_id: $product_id
      quantity: $quantity
      type: $type
      source: $source
      extend_plan_id: $extend_plan_id
    ) {
      id
      quantity
      product {
        id
        sku
        slug
        name
        price
        is_swatch
        fabric {
          id
          is_available_on_pdp
          name
          image
          collaborator {
            id
            name
          }
          material {
            id
            name
          }
        }
        leg {
          id
          name
        }
        frame {
          id
          name
          orientation
          size {
            id
            is_available_on_pdp
            name
          }
          subcategories {
            id
            category {
              id
              name
            }
          }
        }
        images {
          id
          path
        }
      }
    }
  }
`;

export const GET_ALL_CART_PRODUCTS = gql`
  query getAllCartProducts {
    getAllCartProducts {
      id
      quantity
      shippingService {
        id
        name
        price
      }
      product {
        id
        sku
        slug
        name
        price
        is_swatch
        fabric {
          id
          is_available_on_pdp
          name
          image
          collaborator {
            id
            name
          }
          material {
            id
            name
          }
        }
        leg {
          id
          name
        }
        frame {
          id
          name
          orientation
          shippingServices {
            id
            name
            price
          }
          size {
            id
            is_available_on_pdp
            name
          }
          subcategories {
            id
            category {
              id
              name
            }
          }
        }
        images {
          id
          path
        }
      }
    }
  }
`;

const NOTIFY_KLAVIYO = gql`
  mutation notifyKlaviyo($email: String!, $event: String!, $product_id: ID) {
    notifyKlaviyo(email: $email, event: $event, product_id: $product_id) {
      success
    }
  }
`;
